import { useCallback, useMemo } from "react";
import {
  OnboardingDetailedStep,
  OnboardingStep,
} from "@shared/features/onboarding/types";
import { PREVIOUS_STEP_TO_BACK_BUTTON_TITLE } from "@shared/features/onboarding/constants";
import { sharedLib } from "@shared/services/shared.service";

interface useSharedOnboardingStepsProps {
  step?: OnboardingDetailedStep;
  changeStep: (step: OnboardingDetailedStep) => void;
}

const useSharedOnboardingSteps = ({
  step,
  changeStep,
}: useSharedOnboardingStepsProps) => {
  const [finishOnboarding, { isLoading: finishOnboardingLoading }] =
    sharedLib.sharedApi.authentication.useFinishOnboardingMutation();

  const { highLevelStep, currentStepIndex, previousStep } = useMemo(() => {
    if (step === undefined) return {};
    const highLevelStep = DETAILED_STEP_TO_STEP[step];
    let currentStepIndex: number | undefined;
    const previousStep = STEP_TO_PREVIOUS_STEP[step];
    if (highLevelStep !== undefined) {
      currentStepIndex = STEPS_ORDER.indexOf(highLevelStep);
    }
    return {
      highLevelStep,
      currentStepIndex,
      previousStep,
    };
  }, [step]);

  const backButtonTitle = previousStep
    ? PREVIOUS_STEP_TO_BACK_BUTTON_TITLE[previousStep]
    : undefined;

  const onBackButtonClick = useCallback(() => {
    if (previousStep) changeStep(previousStep);
    return true;
  }, [previousStep]);

  const showBackButton = !!previousStep;

  return {
    highLevelStep,
    currentStepIndex,
    previousStep,
    backButtonTitle,
    onBackButtonClick,
    finishOnboardingLoading,
    finishOnboarding,
    showBackButton,
  };
};

export default useSharedOnboardingSteps;

const STEPS_ORDER = [
  OnboardingStep.CONNECT_EXCHANGE,
  OnboardingStep.TRADING_METHOD,
  OnboardingStep.METHOD_SETTINGS,
  OnboardingStep.OVERVIEW,
];
const STEP_TO_PREVIOUS_STEP: {
  [key in OnboardingDetailedStep]?: OnboardingDetailedStep;
} = {
  [OnboardingDetailedStep.CONNECT_EXCHANGE]:
    OnboardingDetailedStep.SELECT_EXCHANGE,
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]:
    OnboardingDetailedStep.TRADING_METHOD,
  [OnboardingDetailedStep.COPY_DCA_BOT]: OnboardingDetailedStep.TRADING_METHOD,
  [OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT]:
    OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
  [OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW]:
    OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
  [OnboardingDetailedStep.DCA_BOT_OVERVIEW]:
    OnboardingDetailedStep.COPY_DCA_BOT,
};
const DETAILED_STEP_TO_STEP: {
  [key in OnboardingDetailedStep]?: OnboardingStep;
} = {
  [OnboardingDetailedStep.SELECT_EXCHANGE]: OnboardingStep.CONNECT_EXCHANGE,
  [OnboardingDetailedStep.CONNECT_EXCHANGE]: OnboardingStep.CONNECT_EXCHANGE,
  [OnboardingDetailedStep.TRADING_METHOD]: OnboardingStep.TRADING_METHOD,
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]: OnboardingStep.METHOD_SETTINGS,
  [OnboardingDetailedStep.COPY_DCA_BOT]: OnboardingStep.METHOD_SETTINGS,
  [OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT]:
    OnboardingStep.METHOD_SETTINGS,
  [OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW]: OnboardingStep.OVERVIEW,
  [OnboardingDetailedStep.DCA_BOT_OVERVIEW]: OnboardingStep.OVERVIEW,
} as const;
